import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, Pause, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "wouter";
import type { MarketingVideo } from "@shared/schema";

const AUTO_ADVANCE_MS = 12000;

export function VideoShowcase() {
  const { data: videos, isLoading } = useQuery<MarketingVideo[]>({
    queryKey: ["/api/marketing-videos"],
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const activeVideos = videos || [];
  const safeIndex = activeVideos.length > 0
    ? Math.min(currentIndex, activeVideos.length - 1)
    : 0;
  const current = activeVideos[safeIndex];

  useEffect(() => {
    if (safeIndex !== currentIndex) {
      setCurrentIndex(safeIndex);
    }
  }, [safeIndex, currentIndex]);

  const goTo = useCallback((index: number) => {
    setCurrentIndex(index);
    setIsPlaying(true);
  }, []);

  const goNext = useCallback(() => {
    if (activeVideos.length <= 1) return;
    goTo((currentIndex + 1) % activeVideos.length);
  }, [currentIndex, activeVideos.length, goTo]);

  const goPrev = useCallback(() => {
    if (activeVideos.length <= 1) return;
    goTo((currentIndex - 1 + activeVideos.length) % activeVideos.length);
  }, [currentIndex, activeVideos.length, goTo]);

  useEffect(() => {
    if (videoRef.current && current) {
      videoRef.current.load();
      if (isPlaying) {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [currentIndex, current]);

  useEffect(() => {
    if (!isPlaying || activeVideos.length <= 1) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(goNext, AUTO_ADVANCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [isPlaying, currentIndex, activeVideos.length, goNext]);

  const togglePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  if (isLoading) {
    return (
      <div className="w-full aspect-video bg-muted/30 rounded-md animate-pulse" />
    );
  }

  if (!activeVideos.length || !current) {
    return null;
  }

  return (
    <div className="relative w-full" data-testid="video-showcase">
      <div className="relative w-full aspect-video rounded-md overflow-hidden bg-black/5 dark:bg-black/20">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          muted
          playsInline
          loop={activeVideos.length === 1}
          onEnded={activeVideos.length > 1 ? goNext : undefined}
          data-testid="video-showcase-player"
        >
          <source src={current.videoUrl} type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
          <div className="flex items-end justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-white text-lg sm:text-xl font-semibold mb-1 truncate" data-testid="text-video-title">
                {current.title}
              </h3>
              {current.description && (
                <p className="text-white/80 text-xs sm:text-sm line-clamp-2 max-w-lg" data-testid="text-video-description">
                  {current.description}
                </p>
              )}
              {current.ctaText && current.ctaLink && (
                <Link href={current.ctaLink}>
                  <Button size="sm" className="mt-3 bg-white text-primary font-medium" data-testid="button-video-cta">
                    {current.ctaText}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="icon"
                variant="ghost"
                className="text-white bg-white/10 backdrop-blur-sm"
                onClick={togglePlayPause}
                data-testid="button-video-play-pause"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>

        {activeVideos.length > 1 && (
          <>
            <Button
              size="icon"
              variant="ghost"
              className="absolute left-2 top-1/2 -translate-y-1/2 text-white bg-black/20 backdrop-blur-sm"
              onClick={goPrev}
              data-testid="button-video-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white bg-black/20 backdrop-blur-sm"
              onClick={goNext}
              data-testid="button-video-next"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </>
        )}
      </div>

      {activeVideos.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3" data-testid="video-showcase-dots">
          {activeVideos.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`w-2 h-2 rounded-full transition-all ${
                i === currentIndex
                  ? "bg-primary w-6"
                  : "bg-muted-foreground/30"
              }`}
              aria-label={`Go to video ${i + 1}`}
              data-testid={`button-video-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
